import type {
  AnalysisPlugin, AppInfo, Annotation, GazeSample, ObjectFrame, LiveState, DepthFrames, ExporterInfo, FrameIndexResponse, HandFrames,
  Job, Ontology, QualityReport, ScanResult, SessionDetail, SessionSummary, SyncInfo, TimeMapping,
  UploadReceipt, ValidationReport, WaveformData,
} from "./types";

import { demoFetch } from "./demoFetch";
const BASE = "/api/v1";

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly detail?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await demoFetch(BASE + path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event("argus-session-expired"));
    let detail: unknown = await res.text();
    try {
      detail = JSON.parse(detail as string);
    } catch { /* plain text body */ }
    const message =
      typeof detail === "object" && detail && "detail" in (detail as any)
        ? String((detail as any).detail)
        : `${res.status} ${res.statusText}`;
    throw new ApiError(message, res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });

export const api = {
  createUpload: (name: string, files: { role: string; name: string; size_bytes: number }[]) => post<UploadReceipt>("/uploads", { name, files }),
  uploadStatus: (id: string) => request<UploadReceipt>(`/uploads/${id}`),
  completeUpload: (id: string) => post<UploadReceipt>(`/uploads/${id}/complete`),
  discardUpload: (id: string) => request(`/uploads/${id}`, {method: "DELETE"}),
  live: () => request<LiveState>("/live"),
  discover: () => request<{devices: {name: string; hostname: string; address: string}[]; available: boolean; reason?: string}>("/live/discover"),
  connect: (address: string, auto_import: boolean, analyze: boolean) => post<LiveState>("/live/connect", {address, auto_import, analyze}),
  disconnect: () => post("/live/disconnect"),
  receive: (id: string) => post<{job: Job}>(`/live/recordings/${id}/receive`),
  pipeline: (id: string) => post<{job: Job}>(`/sessions/${id}/pipeline`),
  gaze: (id: string) => request<{samples: GazeSample[]; count: number; stride: number}>(`/sessions/${id}/gaze`),
  faceFrames: (id: string) => request<{frames: {eye: "left" | "right"; session_ns: number; faces: {box: number[]; confidence: number; keypoints: number[][]}[]}[]}>(`/sessions/${id}/analysis/faces.mediapipe/frames`),
  useCalibration: (id: string, source: string) => post<{job: Job}>(`/sessions/${id}/analysis/use-calibration`, {source_session_id: source}),
  calibrate: (id: string, settings: Record<string, number>) => post<{job: Job}>(`/sessions/${id}/analysis/checkerboard`, {settings}),
  objectFrames: (id: string) => request<{frames: ObjectFrame[]; summary: Record<string, any>}>(`/sessions/${id}/analysis/objects.yolox/frames`),
  app: () => request<AppInfo>("/app"),
  ontology: () => request<Ontology>("/ontology"),
  health: () => request<{ ok: boolean }>("/health"),

  browse: (path: string) =>
    request<{ path: string; parent: string | null; entries: { name: string; path: string; is_dir: boolean; size_bytes: number | null }[] }>(
      `/import/browse?path=${encodeURIComponent(path)}`,
    ),
  scan: (path: string, recursive = true) => post<ScanResult>("/import/scan", { path, recursive }),
  commit: (body: { path: string; keys?: string[]; recursive?: boolean; import_mode?: string; task?: string }) =>
    post<{ sessions: SessionSummary[]; count: number }>("/import/commit", body),
  manualPair: (body: { left: string; right: string; key?: string }) =>
    post<{ session: SessionSummary }>("/import/manual-pair", body),
  rescan: () => post<{ found: number; restored: number }>("/import/rescan-workspace"),

  sessions: (params: { search?: string; status?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set("search", params.search);
    if (params.status) q.set("status", params.status);
    return request<{ sessions: SessionSummary[]; count: number }>(`/sessions?${q}`);
  },
  session: (id: string) => request<SessionDetail>(`/sessions/${id}`),
  patchSession: (id: string, body: Record<string, unknown>) =>
    request<{ session: SessionSummary }>(`/sessions/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteSession: (id: string, deleteFiles = false) =>
    request<{ deleted: string }>(`/sessions/${id}?delete_files=${deleteFiles}`, { method: "DELETE" }),
  frames: (id: string, role: string) => request<FrameIndexResponse>(`/sessions/${id}/frames/${role}`),
  waveform: (id: string) => request<WaveformData>(`/sessions/${id}/waveform`),

  sync: (id: string) =>
    request<{ sync: SyncInfo; mappings: Record<string, TimeMapping>; series: { left_ns: number[]; delta_ms: number[]; valid: boolean[]; stride?: number; count?: number } }>(
      `/sessions/${id}/sync`,
    ),
  setOffset: (id: string, body: { stream_id: string; offset_ns: number; drift?: number; source?: string; method?: string }) =>
    post<{ mapping: TimeMapping; sync: SyncInfo }>(`/sessions/${id}/sync/offset`, body),
  estimateSync: (id: string, apply: boolean) =>
    post<{ job: Job }>(`/sessions/${id}/sync/estimate?apply=${apply}`),
  resetSync: (id: string) => post<{ sync: SyncInfo }>(`/sessions/${id}/sync/reset`),

  annotations: (id: string) => request<{ annotations: Annotation[]; count: number }>(`/sessions/${id}/annotations`),
  createAnnotation: (id: string, body: Partial<Annotation>) =>
    post<{ annotation: Annotation }>(`/sessions/${id}/annotations`, body),
  updateAnnotation: (id: string, annId: string, body: Partial<Annotation>) =>
    request<{ annotation: Annotation }>(`/sessions/${id}/annotations/${annId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteAnnotation: (id: string, annId: string) =>
    request<{ deleted: string }>(`/sessions/${id}/annotations/${annId}`, { method: "DELETE" }),
  restoreAnnotation: (id: string, annId: string) =>
    post<{ annotation: Annotation }>(`/sessions/${id}/annotations/${annId}/restore`),
  exportAnnotationsUrl: (id: string) => `${BASE}/sessions/${id}/annotations/export.jsonl`,

  analysis: (id: string) => request<{ plugins: AnalysisPlugin[] }>(`/sessions/${id}/analysis`),
  runAnalysis: (id: string, pluginId: string, settings: Record<string, unknown>, force = false) =>
    post<{ job: Job; cache_key: string; reused: boolean }>(
      `/sessions/${id}/analysis/${pluginId}`, { settings, force },
    ),
  estimateExtrinsics: (id: string, settings: Record<string, unknown> = {}) =>
    post<{ job: Job }>(`/sessions/${id}/analysis/estimate-extrinsics`, { settings }),
  handFrames: (id: string) => request<HandFrames>(`/sessions/${id}/analysis/hands.mediapipe/frames`),
  denseDepthFrames: (id: string) => request<{metric:false;every_frame:boolean;analysed:number;frame_url?:string;video:{url:string;playback_offset_ns:number}}>(`/sessions/${id}/analysis/depth.relative/frames`),
  depthFrames: (id: string) => request<DepthFrames>(`/sessions/${id}/analysis/depth.stereo_sgbm/frames`),
  depthAt: (id: string, sampleIndex: number, x: number, y: number) =>
    request<{ depth_m: number | null; valid: boolean; pixel: [number, number] }>(
      `/sessions/${id}/analysis/depth.stereo_sgbm/depth_at?sample_index=${sampleIndex}&x=${x}&y=${y}`,
    ),
  pointCloudUrl: (id: string, sampleIndex: number) =>
    `${BASE}/sessions/${id}/analysis/depth.stereo_sgbm/pointcloud/${sampleIndex}.ply`,

  validate: (id: string, verifyChecksums = false) =>
    request<ValidationReport>(`/sessions/${id}/validate?verify_checksums=${verifyChecksums}`),
  exporters: (id: string) =>
    request<{ exporters: ExporterInfo[]; validation: { ok: boolean; errors: number; warnings: number } }>(
      `/sessions/${id}/export`,
    ),
  runExport: (id: string, exporterId: string, options: Record<string, unknown> = {}) =>
    post<{ job: Job }>(`/sessions/${id}/export/${exporterId}`, { destination: "", options }),
  quality: (id: string) => request<QualityReport>(`/sessions/${id}/quality`),
  runQuality: (id: string, options: Record<string, unknown> = {}) =>
    post<{ job: Job }>(`/sessions/${id}/quality`, { destination: "", options }),

  jobs: (params: { session_id?: string; active_only?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.session_id) q.set("session_id", params.session_id);
    if (params.active_only) q.set("active_only", "true");
    return request<{ jobs: Job[] }>(`/jobs?${q}`);
  },
  job: (id: string) => request<{ job: Job }>(`/jobs/${id}`),
  resumeJob: (id: string) => post<{ job: Job }>(`/jobs/${id}/resume`),
  cancelJob: (id: string) => post<{ cancelled: string }>(`/jobs/${id}/cancel`),
  jobEventsUrl: () => `${BASE}/jobs/events`,

  settings: () => request<Record<string, unknown>>("/settings"),
  putSetting: (key: string, value: unknown) =>
    request<{ key: string }>("/settings", { method: "PUT", body: JSON.stringify({ key, value }) }),
};
